// Utility functions for handling time formatting and conversion

/**
 * Convert 12-hour time format (e.g., "04:00 PM") to 24-hour format (e.g., "16:00")
 * @param {string} time12h - Time in 12-hour format like "04:00 PM"
 * @returns {string} - Time in 24-hour format like "16:00"
 */
export const convert12to24 = (time12h) => {
  if (!time12h || typeof time12h !== 'string') {
    return '00:00';
  }

  const trimmed = time12h.trim();
  
  // Check if it's already in 24-hour format (has no AM/PM and hours > 12)
  // or if it's a simple HH:MM format without AM/PM
  if (!trimmed.toUpperCase().includes('AM') && !trimmed.toUpperCase().includes('PM')) {
    // Already in 24-hour format or invalid format
    const [hours, minutes] = trimmed.split(':');
    const hourNum = parseInt(hours, 10);
    if (!isNaN(hourNum) && hourNum >= 0 && hourNum <= 23) {
      return trimmed.includes(':') ? trimmed : '00:00';
    }
    return '00:00';
  }

  const parts = trimmed.split(' ');
  const time = parts[0];
  const modifier = parts[1] ? parts[1].toUpperCase() : '';
  
  if (!time || !time.includes(':')) {
    return '00:00';
  }
  
  let [hours, minutes] = time.split(':');
  const hourNum = parseInt(hours, 10);
  
  if (isNaN(hourNum) || hourNum < 1 || hourNum > 12) {
    return '00:00';
  }
  
  if (modifier === 'PM') {
    if (hourNum !== 12) {
      hours = (hourNum + 12).toString();
    } else {
      hours = '12';
    }
  } else if (modifier === 'AM') {
    if (hourNum === 12) {
      hours = '00';
    } else {
      hours = hourNum.toString().padStart(2, '0');
    }
  } else {
    // No valid modifier, assume it's already 24-hour or return default
    return '00:00';
  }
  
  return `${hours.padStart(2, '0')}:${minutes || '00'}`;
};

/**
 * Create a proper datetime string from separate date and time
 * @param {string} dateStr - Date in ISO format (e.g., "2025-01-01T00:00:00.000Z")
 * @param {string} timeStr - Time in 12-hour format (e.g., "04:00 PM")
 * @returns {string} - Combined datetime in ISO format
 */
export const combineDateAndTime = (dateStr, timeStr) => {
  try {
    if (!dateStr) {
      return new Date().toISOString();
    }

    // Extract just the date part (YYYY-MM-DD) from the ISO string
    const dateOnly = dateStr.split('T')[0];
    
    if (!timeStr || timeStr === 'test' || timeStr.trim() === '') {
      // If no valid time, use noon as default
      return `${dateOnly}T12:00:00`;
    }

    // Convert 12-hour time to 24-hour format
    const time24h = convert12to24(timeStr.trim());
    
    // Combine date and time
    return `${dateOnly}T${time24h}:00`;
  } catch (error) {
    console.error('Error combining date and time:', error);
    return new Date().toISOString();
  }
};

/**
 * Format event datetime for display
 * @param {string} datetime - Datetime string
 * @returns {string} - Formatted datetime string
 */
export const formatEventDateTime = (datetime) => {
  try {
    const eventDate = new Date(datetime);
    if (isNaN(eventDate.getTime())) {
      return 'Date not available';
    }
    
    // Format date
    const dateStr = eventDate.toLocaleDateString();
    
    // Format time in 12-hour format with AM/PM
    const timeStr = eventDate.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
    
    return `${dateStr} • ${timeStr}`;
  } catch (error) {
    return 'Date not available';
  }
};

/**
 * Display the original API time directly (for cases where we want to show exact API time)
 * @param {string} dateStr - Date string
 * @param {string} timeStr - Time string from API (e.g., "04:00 PM")
 * @returns {string} - Formatted display string
 */
export const formatEventDateTimeFromAPI = (dateStr, timeStr) => {
  try {
    if (!dateStr) {
      return 'Date not available';
    }

    const eventDate = new Date(dateStr);
    if (isNaN(eventDate.getTime())) {
      return 'Date not available';
    }
    
    // Format date
    const dateFormatted = eventDate.toLocaleDateString();
    
    // Use the original API time if available, otherwise extract from date
    const timeFormatted = timeStr && timeStr !== 'test' ? timeStr : 
      eventDate.toLocaleTimeString('en-US', { 
        hour: 'numeric', 
        minute: '2-digit',
        hour12: true 
      });
    
    return `${dateFormatted} • ${timeFormatted}`;
  } catch (error) {
    return 'Date not available';
  }
};
